import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { User } from '../classes/user.class';
import { PeriodType } from '../environment';
import { DAY_IN_MILLISECONDS, Utils } from '../utils.class';
import { AuthorisationService } from './auth/authorisation.service';
import { Functionalities } from './auth/functionalities';

type BeginAndEndDate = {
    beginDate: Date;
    endDate: Date;
};

type PeriodDateInfo = BeginAndEndDate & {
    year: number;
    period: number;
    nextMonth?: Date;
};

@Injectable({ providedIn: 'root' })
export class PeriodService {
    getPeriodDateInfo({ period, year }: Readonly<{ period: number; year: number }>) {
        if (environment.periodType === PeriodType.FourWeeks) {
            return this.get4weekPeriodDateInfo({ period, year });
        }

        return this.getMonthPeriodDateInfo({ month: period, year });
    }

    private get4weekPeriodDateInfo({ period, year }: Readonly<{ period: number; year: number }>): PeriodDateInfo {
        const today = new Date();

        Utils.setTime(today, 0, 0);

        // When no explicit period is requested, derive it from the current ISO week.
        if (period === null || period === undefined || Number.isNaN(period)) {
            if (!AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)) {
                today.setDate(today.getDate() - 56);
            }
            if (year === null || year === undefined) {
                // ISO week-numbering year (may differ from the calendar year around new year).
                year = +formatDate(today, 'YYYY', 'nl');
            }
            const currentWeek = +formatDate(today, 'w', 'nl');

            period = Math.floor((currentWeek - 1) / 4) + 1;
        }
        if (year === null || year === undefined || Number.isNaN(year)) {
            year = today.getFullYear();
        }
        // Monday of ISO week 1 (the week that contains 4 January) of the given year.
        const mondayWeek1 = this.getMondayOfIsoWeek1(year);

        // Each period spans 4 ISO weeks (28 days).
        const beginDate = new Date(mondayWeek1);

        beginDate.setDate(beginDate.getDate() + (period - 1) * 28);

        // The last period (13) runs until ISO week 1 of the next year, so a 53rd
        // ISO week (in long years) is absorbed into period 13 instead of being lost.
        const endDate = period >= 13 ? this.getMondayOfIsoWeek1(year + 1) : new Date(beginDate.getTime());

        if (period < 13) {
            endDate.setDate(endDate.getDate() + 28);
        }

        endDate.setMilliseconds(-1);
        return { beginDate, endDate, year, period };
    }

    /** Monday of ISO week 1 (the week that contains 4 January) of the given year. */
    private getMondayOfIsoWeek1(year: number): Date {
        const mondayWeek1 = new Date(year, 0, 4);

        Utils.setTime(mondayWeek1, 0, 0);

        const isoDay = mondayWeek1.getDay() === 0 ? 7 : mondayWeek1.getDay();

        mondayWeek1.setDate(mondayWeek1.getDate() - (isoDay - 1));

        return mondayWeek1;
    }

    private getMonthPeriodDateInfo({ month, year }: Readonly<{ month: number; year: number }>): PeriodDateInfo {
        const today = new Date();

        Utils.setTime(today, 0, 0);

        if (year !== null && year !== undefined && !Number.isNaN(year)) {
            today.setFullYear(year);
        }

        if (month !== null && month !== undefined && !Number.isNaN(month)) {
            if (month > 12) {
                month = 1;
                ++year;

                today.setMonth(0);
                today.setFullYear(year);
            } else if (month < 1) {
                month = 12;
                --year;

                today.setMonth(11);
                today.setFullYear(year);
            } else {
                today.setMonth(month - 1);
            }
        }

        Utils.setTime(today, 0, 0);

        const beginDate = new Date(today);
        beginDate.setDate(1);

        const endDate = new Date(beginDate);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);

        const nextMonth = new Date(beginDate);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        return { beginDate, endDate, year: beginDate.getFullYear(), period: beginDate.getMonth() + 1, nextMonth };
    }

    getFullWeeksPeriodDates({ beginDate, endDate }: Readonly<BeginAndEndDate>): BeginAndEndDate {
        if (environment.periodType === PeriodType.FourWeeks) {
            return { beginDate, endDate };
        }

        let newBeginDate = new Date(beginDate);
        const daysToSubtract = (newBeginDate.getDay() + 6) % 7;

        if (daysToSubtract > 0) {
            newBeginDate.setDate(newBeginDate.getDate() - daysToSubtract);
            newBeginDate = new Date(newBeginDate);
        }

        let newEndDate = new Date(endDate);
        const daysToAdd = (7 - newEndDate.getDay()) % 7;

        if (daysToAdd > 0) {
            newEndDate.setDate(newEndDate.getDate() + daysToAdd);
            newEndDate = new Date(newEndDate);
        }

        return { beginDate: newBeginDate, endDate: newEndDate };
    }

    /**
     * Valt een datum binnen de periode zelf? De weken van de verrekening lopen buiten de periode
     * door (zie {@link getFullWeeksPeriodDates}): die dagen worden wel verloond, maar mogen niet
     * meetellen in de totalen die de periode tonen.
     *
     * De grenzen worden op hele dagen vergeleken, zodat een tijdstip op de begin- of einddatum
     * geen invloed heeft: de laatste dag van de periode valt er volledig in.
     */
    isDateInPeriod(date: Readonly<Date>, { beginDate, endDate }: Readonly<BeginAndEndDate>) {
        const timestamp = this.getDateOnlyTimestamp(date);

        return timestamp >= this.getDateOnlyTimestamp(beginDate) && timestamp <= this.getDateOnlyTimestamp(endDate);
    }

    getEmployeeContractHoursPerPeriod(employee: Readonly<User>, beginAndEndDate: Readonly<BeginAndEndDate>) {
        const contractHoursPerWeek = employee?.contract_hours ?? 0;

        if (!contractHoursPerWeek) {
            return 0;
        }

        const { beginDate } = beginAndEndDate;
        let { endDate } = beginAndEndDate;

        if (this.isExclusiveFourWeekEndDate(beginDate, endDate)) {
            endDate = this.addDays(endDate, -1);
        }

        const contractHours = this.countContractHours({ beginDate, endDate }, contractHoursPerWeek);

        return contractHours;
    }

    private isExclusiveFourWeekEndDate(beginDate: Readonly<Date>, endDate: Readonly<Date>) {
        return (
            environment.periodType === PeriodType.FourWeeks &&
            beginDate.getDay() === 1 &&
            endDate.getDay() === 1 &&
            this.getDateOnlyTimestamp(endDate) > this.getDateOnlyTimestamp(beginDate)
        );
    }

    private countContractHours({ beginDate, endDate }: Readonly<BeginAndEndDate>, contractHoursPerWeek: number) {
        const msPerDay = DAY_IN_MILLISECONDS;
        const beginTimestamp = this.getDateOnlyTimestamp(beginDate);
        const endTimestamp = this.getDateOnlyTimestamp(endDate);

        if (endTimestamp < beginTimestamp) {
            return 0;
        }

        const days = Math.floor((endTimestamp - beginTimestamp) / msPerDay) + 1;
        const fullWeeks = Math.floor(days / 7);
        const remainingDays = days % 7;
        const beginIsoDay = this.getIsoDay(beginDate);

        let contractHours = fullWeeks * contractHoursPerWeek;

        for (let day = 0; day < remainingDays; ++day) {
            const isoDay = ((beginIsoDay + day - 1) % 7) + 1;

            contractHours += this.getContractHoursForIsoDay(contractHoursPerWeek, isoDay);
        }

        return contractHours;
    }

    private getContractHoursForIsoDay(contractHoursPerWeek: number, isoDay: number) {
        const hoursLeftFromDay = contractHoursPerWeek - (isoDay - 1) * 8;

        return Math.min(Math.max(hoursLeftFromDay, 0), 8);
    }

    private getIsoDay(date: Readonly<Date>) {
        return date.getDay() === 0 ? 7 : date.getDay();
    }

    private getDateOnlyTimestamp(date: Readonly<Date>) {
        return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    }

    private addDays(date: Readonly<Date>, days: number) {
        const newDate = new Date(date);

        newDate.setDate(newDate.getDate() + days);

        return newDate;
    }
}
